import { useState, useEffect, useMemo, useCallback } from "react";
import { Routes, Route, Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { NativeSelect } from "@/components/ui/native-select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Plus, ArrowLeft, Edit, Trash2, FileText, Search, Eye, 
  Check, X, Upload, Download, Play, Square, DollarSign,
  Building, Calendar, FileCheck, AlertCircle, Clock
} from "lucide-react";
import { extractErrorMessage } from "@/lib/errorMessage";
import { MaterialConsumidoCard } from "@/components/estoque/MaterialConsumidoCard";
import { gerarRelatorioObraPDF } from "@/lib/obraPDF";
import { PdfPreviewDialog } from "@/components/financeiro/contas/ContasDialogs";

function formatCurrency(value) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0);
}

function formatDate(dateStr) {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString('pt-BR');
}

const statusConfig = {
  rascunho: { label: "Rascunho", class: "bg-slate-100 text-slate-700" },
  ativo: { label: "Ativo", class: "bg-green-100 text-green-700" },
  concluido: { label: "Concluído", class: "bg-blue-100 text-blue-700" },
  cancelado: { label: "Cancelado", class: "bg-red-100 text-red-700" }
};

function ContratosList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    fetchItems();
  }, [filterStatus]);

  const fetchItems = async () => {
    try {
      let url = `${API}/contratos`;
      if (filterStatus && filterStatus !== "all") {
        url += `?status=${filterStatus}`;
      }
      const response = await axios.get(url);
      setItems(response.data);
    } catch (error) {
      toast.error("Erro ao carregar contratos");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/contratos/${id}`);
      toast.success("Contrato excluído");
      fetchItems();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erro ao excluir"));
    }
  };

  const handleAtivar = async (id) => {
    try {
      await axios.post(`${API}/contratos/${id}/ativar`);
      toast.success("Contrato ativado e parcelas geradas!");
      fetchItems();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erro ao ativar"));
    }
  };

  const filteredItems = items.filter(item =>
    item.titulo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.cliente_nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.numero?.toString().includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-luvcorp-navy"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="contratos-list">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">Contratos</h1>
          <p className="text-slate-500">Gestão de contratos de serviço</p>
        </div>
        <Link to="/comercial/contratos/new">
          <Button className="bg-luvcorp-gold hover:bg-luvcorp-gold-dark text-white" data-testid="new-btn">
            <Plus className="h-4 w-4 mr-2" />
            Novo Contrato
          </Button>
        </Link>
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Buscar por título, cliente ou número..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <NativeSelect
              value={filterStatus}
              onValueChange={setFilterStatus}
              placeholder="Status"
              options={[
                { value: "all", label: "Todos" },
                { value: "rascunho", label: "Rascunho" },
                { value: "ativo", label: "Ativo" },
                { value: "concluido", label: "Concluído" },
                { value: "cancelado", label: "Cancelado" }
              ]}
              className="w-[180px]"
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[80px]">Nº</TableHead>
                <TableHead>Nº de Proposta</TableHead>
                <TableHead>Cliente</TableHead>
                <TableHead className="text-right">Valor</TableHead>
                <TableHead>Vigência</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-[150px]">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-mono font-medium">#{item.numero}</TableCell>
                  <TableCell className="font-medium">{item.titulo}</TableCell>
                  <TableCell>{item.cliente_nome}</TableCell>
                  <TableCell className="text-right font-medium">{formatCurrency(item.valor_total)}</TableCell>
                  <TableCell>
                    <span className="text-sm">
                      {formatDate(item.data_inicio)} - {formatDate(item.data_fim)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge className={statusConfig[item.status]?.class || "bg-slate-100"}>
                      {statusConfig[item.status]?.label || item.status}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Link to={`/comercial/contratos/${item.id}`}>
                        <Button variant="ghost" size="sm" title="Ver detalhes">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </Link>
                      {item.status === "rascunho" && (
                        <>
                          <Link to={`/comercial/contratos/${item.id}/edit`}>
                            <Button variant="ghost" size="sm" title="Editar">
                              <Edit className="h-4 w-4" />
                            </Button>
                          </Link>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            title="Ativar Contrato"
                            onClick={() => handleAtivar(item.id)}
                          >
                            <Play className="h-4 w-4 text-green-600" />
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-red-500">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Excluir contrato?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Esta ação não pode ser desfeita.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(item.id)}
                                  className="bg-red-600 hover:bg-red-700"
                                >
                                  Excluir
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredItems.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                    <FileCheck className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    Nenhum contrato encontrado
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function ContratoForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [centrosCusto, setCentrosCusto] = useState([]);
  const [contas, setContas] = useState([]);
  const [orcamento, setOrcamento] = useState(null);
  const [formData, setFormData] = useState({
    orcamento_id: "",
    cliente_id: "",
    titulo: "",
    descricao: "",
    valor_total: 0,
    valor_sinal: 0,
    data_sinal: "",
    data_primeira_parcela: "",
    parcelas: 1,
    intervalo_parcelas: 30,
    tipo_vencimento: "intervalo",  // "intervalo" ou "dia_fixo"
    dia_vencimento_fixo: 5,        // 1-31
    data_inicio: new Date().toISOString().split('T')[0],
    vigencia_meses: 12,
    indice_reajuste: "none",
    percentual_reajuste: 0,
    centro_custo_id: "",
    conta_id: "",
    arquivo_contrato: null,         // {filename, content_base64, content_type, size}
  });

  const isEdit = !!id;
  const orcamentoId = searchParams.get('orcamento');

  useEffect(() => {
    fetchDependencies();
    if (isEdit) {
      fetchItem();
    } else if (orcamentoId) {
      fetchOrcamento(orcamentoId);
    }
  }, [id, orcamentoId]);

  const fetchDependencies = async () => {
    try {
      const [clientesRes, centrosRes, contasRes] = await Promise.all([
        axios.get(`${API}/clientes-fornecedores?tipo=cliente`),
        axios.get(`${API}/centros-custo?ativo=true`),
        axios.get(`${API}/plano-contas?tipo=receita&ativo=true`)
      ]);
      setClientes(clientesRes.data);
      setCentrosCusto(centrosRes.data);
      setContas(contasRes.data);
    } catch (error) {
      toast.error("Erro ao carregar dados");
    }
  };

  const fetchItem = async () => {
    try {
      const response = await axios.get(`${API}/contratos/${id}`);
      setFormData(response.data);
    } catch (error) {
      toast.error("Erro ao carregar contrato");
      navigate("/comercial/contratos");
    }
  };

  const fetchOrcamento = async (orcId) => {
    try {
      const response = await axios.get(`${API}/orcamentos/${orcId}`);
      const orc = response.data;
      setOrcamento(orc);
      setFormData(prev => ({
        ...prev,
        orcamento_id: orc.id,
        cliente_id: orc.cliente_id,
        titulo: orc.titulo,
        descricao: orc.descricao,
        valor_total: orc.valor_total,
        valor_sinal: orc.valor_sinal,
        parcelas: orc.parcelas,
        intervalo_parcelas: orc.intervalo_parcelas,
        centro_custo_id: orc.centro_custo_id || "",
        conta_id: orc.conta_id || ""
      }));
    } catch (error) {
      toast.error("Erro ao carregar orçamento");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.cliente_id) {
      toast.error("Selecione um cliente");
      return;
    }
    setLoading(true);

    try {
      if (isEdit) {
        const r = await axios.put(`${API}/contratos/${id}`, formData);
        toast.success("Contrato atualizado!");
        // Detectar mudancas financeiras em contrato ativo
        if (r.data?.needs_regenerate) {
          const yes = window.confirm(
            "Você alterou campos financeiros (valor, parcelas, datas). " +
            "As parcelas em Contas a Receber NÃO foram recalculadas automaticamente.\n\n" +
            "Deseja regenerar as parcelas pendentes agora?\n\n" +
            "(Parcelas já PAGAS não serão afetadas.)"
          );
          if (yes) {
            try {
              const reg = await axios.post(`${API}/contratos/${id}/regenerar-lancamentos`);
              toast.success(reg.data?.message || "Parcelas regeneradas");
            } catch (err) {
              toast.error(extractErrorMessage(err, "Erro ao regenerar parcelas"));
            }
          } else {
            toast.warning("Lembre-se: clique em 'Regenerar parcelas' depois para sincronizar.");
          }
        }
      } else {
        await axios.post(`${API}/contratos`, formData);
        toast.success("Contrato criado!");
      }
      navigate("/comercial/contratos");
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erro ao salvar"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto" data-testid="contrato-form">
      <div className="flex items-center gap-4 mb-6">
        <Link to="/comercial/contratos">
          <Button variant="ghost" size="icon">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">
          {isEdit ? "Editar Contrato" : "Novo Contrato"}
        </h1>
      </div>

      {orcamento && (
        <Card className="border-0 shadow-sm mb-6 bg-purple-50 border-l-4 border-l-purple-500">
          <CardContent className="p-4">
            <div className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-purple-600" />
              <span className="font-medium">Vinculado ao Orçamento #{orcamento.numero}</span>
            </div>
            <p className="text-sm text-purple-700 mt-1">{orcamento.titulo}</p>
          </CardContent>
        </Card>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Info básica */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Informações do Contrato</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cliente *</Label>
                <NativeSelect
                  value={formData.cliente_id}
                  onValueChange={(value) => setFormData({...formData, cliente_id: value})}
                  placeholder="Selecione o cliente"
                  options={clientes.map(c => ({ value: c.id, label: c.nome }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Número de Proposta *</Label>
                <Input
                  value={formData.titulo}
                  onChange={(e) => setFormData({...formData, titulo: e.target.value})}
                  placeholder="Ex: Contrato de Manutenção Predial"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Descrição / Objeto do Contrato</Label>
              <Textarea
                value={formData.descricao}
                onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                placeholder="Descrição detalhada do objeto contratado..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Vigência */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Vigência do Contrato</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Data de Início *</Label>
                <Input
                  type="date"
                  value={formData.data_inicio}
                  onChange={(e) => setFormData({...formData, data_inicio: e.target.value})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Vigência (meses) *</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.vigencia_meses}
                  onChange={(e) => {
                    const meses = parseInt(e.target.value) || 12;
                    setFormData(prev => ({
                      ...prev,
                      vigencia_meses: meses,
                      // Auto-sugere parcelas = vigencia para contratos mensais
                      parcelas: prev.parcelas === prev.vigencia_meses || prev.parcelas === 1
                        ? meses
                        : prev.parcelas,
                    }));
                  }}
                />
                <p className="text-[10px] text-slate-500">Ao alterar, sugere {formData.vigencia_meses} parcela(s) abaixo</p>
              </div>
              <div className="space-y-2">
                <Label>Data de Término</Label>
                <Input
                  type="date"
                  value={formData.data_inicio ? 
                    new Date(new Date(formData.data_inicio).getTime() + formData.vigencia_meses * 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0] 
                    : ""
                  }
                  disabled
                  className="bg-slate-50"
                />
              </div>
            </div>

            {/* Reajuste */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <p className="text-sm font-medium text-slate-700 mb-3">Reajuste Anual</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Índice de Reajuste</Label>
                  <NativeSelect
                    value={formData.indice_reajuste}
                    onValueChange={(value) => setFormData({...formData, indice_reajuste: value})}
                    placeholder="Selecione o índice"
                    options={[
                      { value: "none", label: "Sem reajuste" },
                      { value: "IGPM", label: "IGPM" },
                      { value: "IPCA", label: "IPCA" },
                      { value: "INPC", label: "INPC" },
                      { value: "INCC", label: "INCC" },
                      { value: "FIXO", label: "Percentual Fixo" }
                    ]}
                  />
                </div>
                {formData.indice_reajuste === "FIXO" && (
                  <div className="space-y-2">
                    <Label>Percentual (%)</Label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.percentual_reajuste}
                      onChange={(e) => setFormData({...formData, percentual_reajuste: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Valores e Pagamento */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Valores e Pagamento</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Valor Total do Contrato *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.valor_total}
                  onChange={(e) => setFormData({...formData, valor_total: parseFloat(e.target.value) || 0})}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Valor do Sinal (Entrada)</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.valor_sinal}
                  onChange={(e) => setFormData({...formData, valor_sinal: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Data Pagamento Sinal</Label>
                <Input
                  type="date"
                  value={formData.data_sinal}
                  onChange={(e) => setFormData({...formData, data_sinal: e.target.value})}
                  data-testid="input-data-sinal"
                />
              </div>
              <div className="space-y-2">
                <Label>Número de Parcelas</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.parcelas}
                  onChange={(e) => setFormData({...formData, parcelas: parseInt(e.target.value) || 1})}
                />
              </div>
              <div className="space-y-2">
                <Label>1ª Parcela (vencimento)</Label>
                <Input
                  type="date"
                  value={formData.data_primeira_parcela}
                  onChange={(e) => setFormData({...formData, data_primeira_parcela: e.target.value})}
                />
              </div>
              <div className="space-y-2 md:col-span-2 lg:col-span-3">
                <Label>Tipo de Vencimento das Parcelas</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, tipo_vencimento: "intervalo"})}
                    className={`flex-1 px-3 py-2 text-sm rounded-md border-2 transition-all ${formData.tipo_vencimento === "intervalo" ? "border-[#0A1628] bg-[#0A1628]/5 text-[#0A1628] font-medium" : "border-slate-200 text-slate-600"}`}
                    data-testid="btn-vencimento-intervalo"
                  >
                    A cada N dias
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormData({...formData, tipo_vencimento: "dia_fixo"})}
                    className={`flex-1 px-3 py-2 text-sm rounded-md border-2 transition-all ${formData.tipo_vencimento === "dia_fixo" ? "border-[#0A1628] bg-[#0A1628]/5 text-[#0A1628] font-medium" : "border-slate-200 text-slate-600"}`}
                    data-testid="btn-vencimento-dia-fixo"
                  >
                    Dia fixo do mês
                  </button>
                </div>
              </div>
              {formData.tipo_vencimento === "intervalo" ? (
                <div className="space-y-2">
                  <Label>Intervalo (dias)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.intervalo_parcelas}
                    onChange={(e) => setFormData({...formData, intervalo_parcelas: parseInt(e.target.value) || 30})}
                    data-testid="input-intervalo-dias"
                  />
                </div>
              ) : (
                <div className="space-y-2">
                  <Label>Vence todo dia (1-31)</Label>
                  <Input
                    type="number"
                    min="1"
                    max="31"
                    value={formData.dia_vencimento_fixo}
                    onChange={(e) => setFormData({...formData, dia_vencimento_fixo: parseInt(e.target.value) || 5})}
                    placeholder="Ex: 5 = todo dia 5"
                    data-testid="input-dia-fixo"
                  />
                </div>
              )}
            </div>

            {/* Preview de parcelas */}
            {formData.valor_total > 0 && (
              <div className="p-4 bg-green-50 rounded-lg">
                <p className="text-sm font-medium text-green-800 mb-2">Previsão de Faturamento:</p>
                <div className="space-y-1 text-sm text-green-700">
                  {formData.valor_sinal > 0 && (
                    <p>• Sinal: {formatCurrency(formData.valor_sinal)} {formData.data_sinal && `(venc. ${formatDate(formData.data_sinal)})`}</p>
                  )}
                  <p>• {formData.parcelas}x de {formatCurrency((formData.valor_total - formData.valor_sinal) / formData.parcelas)} {formData.tipo_vencimento === "dia_fixo" ? `vencendo todo dia ${formData.dia_vencimento_fixo}` : `a cada ${formData.intervalo_parcelas} dias`}</p>
                  {formData.data_primeira_parcela && (
                    <p className="text-green-600">Início: {formatDate(formData.data_primeira_parcela)}</p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Classificação Financeira */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg">Classificação Financeira</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Centro de Custo</Label>
                <NativeSelect
                  value={formData.centro_custo_id}
                  onValueChange={(value) => setFormData({...formData, centro_custo_id: value})}
                  placeholder="Selecione..."
                  options={centrosCusto.map(c => ({ value: c.id, label: `${c.codigo} - ${c.nome}` }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Conta Contábil</Label>
                <NativeSelect
                  value={formData.conta_id}
                  onValueChange={(value) => setFormData({...formData, conta_id: value})}
                  placeholder="Selecione..."
                  options={contas.map(c => ({ value: c.id, label: `${c.codigo} - ${c.nome}` }))}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Upload do Contrato */}
        <Card className="border-0 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2"><Upload className="h-5 w-5" />Arquivo do Contrato</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {formData.arquivo_contrato ? (
              <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200" data-testid="arquivo-anexado">
                <FileText className="h-6 w-6 text-emerald-700 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-emerald-900 truncate">{formData.arquivo_contrato.filename}</p>
                  <p className="text-xs text-emerald-700">{Math.round((formData.arquivo_contrato.size || 0) / 1024)} KB</p>
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => setFormData({...formData, arquivo_contrato: null})} className="text-red-600" data-testid="btn-remover-arquivo">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center p-6 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-[#0A1628] hover:bg-slate-50 transition-colors" data-testid="upload-label">
                <Upload className="h-8 w-8 text-slate-400 mb-2" />
                <p className="text-sm font-medium text-slate-700">Clique para anexar o contrato</p>
                <p className="text-xs text-slate-500 mt-1">PDF, DOC, DOCX, JPG, PNG até 10 MB</p>
                <input
                  type="file"
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  className="hidden"
                  data-testid="input-file-contrato"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    if (file.size > 10 * 1024 * 1024) { toast.error("Arquivo > 10 MB"); return; }
                    const reader = new FileReader();
                    reader.onload = () => {
                      const result = reader.result;
                      const base64 = typeof result === "string" ? result.split(",")[1] : "";
                      setFormData(prev => ({
                        ...prev,
                        arquivo_contrato: {
                          filename: file.name,
                          content_base64: base64,
                          content_type: file.type,
                          size: file.size,
                        }
                      }));
                      toast.success(`${file.name} anexado`);
                    };
                    reader.readAsDataURL(file);
                  }}
                />
              </label>
            )}
            <p className="text-xs text-slate-500">O contrato anexado fica disponível para download a qualquer momento.</p>
          </CardContent>
        </Card>

        <div className="flex gap-4">
          <Button
            type="submit"
            className="flex-1 bg-luvcorp-navy hover:bg-luvcorp-navy-dark text-white h-11"
            disabled={loading}
          >
            {loading ? "Salvando..." : (isEdit ? "Atualizar Contrato" : "Criar Contrato")}
          </Button>
        </div>
      </form>
    </div>
  );
}

function ContratoDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploadingAnexo, setUploadingAnexo] = useState(false);
  const [gerandoPdf, setGerandoPdf] = useState(false);
  const [pdfModal, setPdfModal] = useState({ open: false, blobUrl: null, filename: "" });

  const handlePdfObra = async () => {
    if (gerandoPdf || !item) return;
    setGerandoPdf(true);
    try {
      let custo = { total: 0, movimentacoes: 0, materiais: [] };
      try {
        const r = await axios.get(`${API}/estoque-relatorios/custo-por-obra/${item.id}`);
        custo = r.data;
      } catch { /* sem saídas */ }
      const { blob, filename } = await gerarRelatorioObraPDF({ contrato: item, custoMateriais: custo });
      setPdfModal(prev => {
        if (prev.blobUrl) URL.revokeObjectURL(prev.blobUrl);
        return { open: true, blobUrl: URL.createObjectURL(blob), filename };
      });
    } catch (err) {
      console.error(err);
      toast.error("Erro ao gerar PDF da obra");
    } finally { setGerandoPdf(false); }
  };

  const closePdfObra = () => {
    setPdfModal(prev => {
      if (prev.blobUrl) URL.revokeObjectURL(prev.blobUrl);
      return { open: false, blobUrl: null, filename: "" };
    });
  };

  useEffect(() => {
    fetchItem();
  }, [id]);

  const fetchItem = async () => {
    try {
      const response = await axios.get(`${API}/contratos/${id}`);
      setItem(response.data);
    } catch (error) {
      toast.error("Erro ao carregar contrato");
      navigate("/comercial/contratos");
    } finally {
      setLoading(false);
    }
  };

  const handleUploadAnexo = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error("Arquivo excede 25 MB");
      return;
    }
    setUploadingAnexo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await axios.post(`${API}/contratos/${id}/anexo`, fd);
      toast.success("Anexo enviado com sucesso");
      await fetchItem();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erro ao enviar anexo"));
    } finally {
      setUploadingAnexo(false);
    }
  };

  const handleDownloadAnexo = (anexo) => {
    try {
      const byteChars = atob(anexo.data);
      const bytes = new Uint8Array(byteChars.length);
      for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
      const blob = new Blob([bytes], { type: anexo.tipo || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = anexo.nome || "anexo";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      toast.error("Erro ao baixar anexo");
    }
  };

  const handleAtivar = async () => {
    try {
      const r = await axios.post(`${API}/contratos/${id}/ativar`);
      const n = r.data?.lancamentos_criados ?? 0;
      if (n > 0) toast.success(`Contrato ativado · ${n} parcela(s) gerada(s)!`);
      else toast.warning("Contrato ativado, mas NENHUMA parcela foi gerada. Verifique valor/parcelas/data e use 'Regenerar parcelas'.");
      fetchItem();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erro ao ativar"));
    }
  };

  const handleRegenerar = async () => {
    if (!window.confirm("Isso vai apagar as parcelas pendentes deste contrato e gerar novas. Continuar?")) return;
    try {
      const r = await axios.post(`${API}/contratos/${id}/regenerar-lancamentos`);
      toast.success(r.data?.message || "Parcelas regeneradas");
      fetchItem();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erro ao regenerar parcelas"));
    }
  };

  const handleConcluir = async () => {
    try {
      await axios.post(`${API}/contratos/${id}/concluir`);
      toast.success("Contrato concluído");
      fetchItem();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erro"));
    }
  };

  const handleCancelar = async () => {
    try {
      await axios.post(`${API}/contratos/${id}/cancelar`);
      toast.success("Contrato cancelado");
      fetchItem();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Erro"));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-luvcorp-navy"></div>
      </div>
    );
  }

  if (!item) return null;

  const lancamentosPagos = item.lancamentos?.filter(l => l.status === "pago") || [];
  const lancamentosPendentes = item.lancamentos?.filter(l => l.status === "pendente") || [];
  const totalPago = lancamentosPagos.reduce((acc, l) => acc + l.valor, 0);
  const totalPendente = lancamentosPendentes.reduce((acc, l) => acc + l.valor, 0);

  return (
    <div className="max-w-5xl mx-auto" data-testid="contrato-detail">
      <div className="flex items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Link to="/comercial/contratos">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className="font-mono">#{item.numero}</Badge>
              <Badge className={statusConfig[item.status]?.class}>
                {statusConfig[item.status]?.label}
              </Badge>
            </div>
            <h1 className="text-2xl font-heading font-bold text-luvcorp-navy">{item.titulo}</h1>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={handlePdfObra} variant="outline" disabled={gerandoPdf}
            className="border-luvcorp-navy text-luvcorp-navy hover:bg-luvcorp-navy/5" data-testid="btn-pdf-obra">
            <FileText className="h-4 w-4 mr-2" /> {gerandoPdf ? "Gerando…" : "PDF da Obra"}
          </Button>
          {item.status === "rascunho" && (
            <Button onClick={handleAtivar} className="bg-green-600 hover:bg-green-700 text-white">
              <Play className="h-4 w-4 mr-2" /> Ativar Contrato
            </Button>
          )}
          {item.status === "ativo" && (
            <>
              <Button onClick={handleRegenerar} variant="outline" className="text-amber-700 border-amber-300" data-testid="btn-regenerar">
                <DollarSign className="h-4 w-4 mr-2" /> Regenerar parcelas
              </Button>
              <Button onClick={handleConcluir} variant="outline">
                <Check className="h-4 w-4 mr-2" /> Concluir
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="text-red-600 border-red-200">
                    <X className="h-4 w-4 mr-2" /> Cancelar
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Cancelar contrato?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Isso cancelará todas as parcelas pendentes. Esta ação não pode ser desfeita.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Voltar</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={handleCancelar}
                      className="bg-red-600 hover:bg-red-700"
                    >
                      Confirmar Cancelamento
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          {/* Info */}
          <Card className="border-0 shadow-sm">
            <CardContent className="p-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Cliente</p>
                  <p className="font-medium">{item.cliente?.nome || "-"}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Início</p>
                  <p className="font-medium">{formatDate(item.data_inicio)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Término</p>
                  <p className="font-medium">{formatDate(item.data_fim)}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Vigência</p>
                  <p className="font-medium">{item.vigencia_meses} meses</p>
                </div>
              </div>
              {item.indice_reajuste && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-slate-500">Reajuste Anual</p>
                  <p className="font-medium">
                    {item.indice_reajuste === "FIXO" 
                      ? `${item.percentual_reajuste}% ao ano`
                      : `Pelo ${item.indice_reajuste}`
                    }
                  </p>
                </div>
              )}
              {item.descricao && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-slate-500">Descrição</p>
                  <p className="mt-1">{item.descricao}</p>
                </div>
              )}
              {item.arquivo_contrato?.filename && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-slate-500 mb-2">Arquivo do Contrato</p>
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border" data-testid="arquivo-contrato-anexado">
                    <FileText className="h-6 w-6 text-[#0A1628] shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#0A1628] truncate">{item.arquivo_contrato.filename}</p>
                      <p className="text-xs text-slate-500">{Math.round((item.arquivo_contrato.size || 0) / 1024)} KB</p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const base64 = item.arquivo_contrato.content_base64;
                        const mime = item.arquivo_contrato.content_type || "application/octet-stream";
                        const byteChars = atob(base64);
                        const bytes = new Uint8Array(byteChars.length);
                        for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
                        const blob = new Blob([bytes], { type: mime });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = item.arquivo_contrato.filename;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        setTimeout(() => URL.revokeObjectURL(url), 5000);
                      }}
                      data-testid="btn-download-contrato-arquivo"
                    >
                      <Download className="h-4 w-4 mr-1" />Baixar
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Parcelas */}
          {item.lancamentos?.length > 0 && (
            <Card className="border-0 shadow-sm">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Parcelas</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Parcela</TableHead>
                      <TableHead>Descrição</TableHead>
                      <TableHead>Vencimento</TableHead>
                      <TableHead className="text-right">Valor</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {item.lancamentos.map((lanc) => (
                      <TableRow key={lanc.id}>
                        <TableCell className="font-medium">{lanc.parcela}</TableCell>
                        <TableCell className="text-sm">{lanc.descricao}</TableCell>
                        <TableCell>{formatDate(lanc.data_vencimento)}</TableCell>
                        <TableCell className="text-right font-medium">{formatCurrency(lanc.valor)}</TableCell>
                        <TableCell>
                          <Badge className={
                            lanc.status === "pago" ? "bg-green-100 text-green-700" :
                            lanc.status === "pendente" ? "bg-amber-100 text-amber-700" :
                            "bg-red-100 text-red-700"
                          }>
                            {lanc.status === "pago" ? "Pago" : lanc.status === "pendente" ? "Pendente" : "Cancelado"}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Resumo */}
        <div className="space-y-6">
          <MaterialConsumidoCard contratoId={item.id} />
          <Card className="border-0 shadow-sm bg-luvcorp-navy text-white">
            <CardContent className="p-6">
              <p className="text-sm text-white/70">Valor Total</p>
              <p className="text-3xl font-bold">{formatCurrency(item.valor_total)}</p>
              
              {item.lancamentos?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-white/20 space-y-3">
                  <div>
                    <div className="flex justify-between text-sm">
                      <span className="text-white/70">Pago</span>
                      <span className="text-green-400">{formatCurrency(totalPago)}</span>
                    </div>
                    <div className="w-full bg-white/20 rounded-full h-2 mt-1">
                      <div 
                        className="bg-green-400 h-2 rounded-full transition-all"
                        style={{ width: `${item.valor_total > 0 ? (totalPago / item.valor_total) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white/70">Pendente</span>
                    <span className="text-amber-400">{formatCurrency(totalPendente)}</span>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Cronograma Financeiro (Timeline) */}
          <CronogramaFinanceiro contratoId={id} contratoStatus={item.status} />

          {/* Anexos */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <Upload className="h-4 w-4" /> Anexos
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Upload sempre habilitado, independente do status */}
              <label className="flex flex-col items-center justify-center p-4 mb-3 border-2 border-dashed border-slate-300 rounded-lg cursor-pointer hover:border-luvcorp-navy hover:bg-slate-50/40 transition-colors" data-testid="contrato-upload-label">
                <Upload className="h-5 w-5 text-slate-400 mb-1" />
                <span className="text-xs text-slate-600 font-medium">
                  {uploadingAnexo ? "Enviando..." : "Clique para anexar PDF, imagem ou documento"}
                </span>
                <span className="text-[10px] text-slate-400 mt-0.5">PDF, JPG, PNG, DOCX — até 25 MB</span>
                <input
                  type="file"
                  accept=".pdf,image/*,.docx,.doc,.xlsx,.xls"
                  className="hidden"
                  disabled={uploadingAnexo}
                  onChange={handleUploadAnexo}
                  data-testid="contrato-upload-input"
                />
              </label>

              {item.anexos?.length > 0 ? (
                <div className="space-y-2" data-testid="anexos-list">
                  {item.anexos.map((anexo) => (
                    <div key={anexo.id} className="flex items-center justify-between p-2 bg-slate-50 rounded" data-testid={`anexo-${anexo.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate font-medium">{anexo.nome}</p>
                        <p className="text-[10px] text-slate-500">{(anexo.tamanho / 1024).toFixed(1)} KB · {new Date(anexo.uploaded_at).toLocaleDateString("pt-BR")}</p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => handleDownloadAnexo(anexo)} data-testid={`btn-download-${anexo.id}`}>
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-400 text-center py-3" data-testid="anexos-empty">Nenhum anexo</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <PdfPreviewDialog pdfModal={pdfModal} onClose={closePdfObra} titulo={`Relatório da Obra • #${item.numero || ""} ${item.titulo || ""}`} />
    </div>
  );
}

export default function ContratosPage() {
  return (
    <MainLayout>
      <Routes>
        <Route index element={<ContratosList />} />
        <Route path="new" element={<ContratoForm />} />
        <Route path=":id" element={<ContratoDetail />} />
        <Route path=":id/edit" element={<ContratoForm />} />
      </Routes>
    </MainLayout>
  );
}

// =====================================================
// Cronograma Financeiro - Timeline visual + edição de parcelas
// =====================================================
function CronogramaFinanceiro({ contratoId, contratoStatus }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editValor, setEditValor] = useState("");
  const [editVenc, setEditVenc] = useState("");
  const [adding, setAdding] = useState(false);
  const [newValor, setNewValor] = useState("");
  const [newVenc, setNewVenc] = useState("");
  const [newRotulo, setNewRotulo] = useState("");
  const [saving, setSaving] = useState(false);

  const editavel = contratoStatus === "rascunho" || contratoStatus === "ativo";

  const reload = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/contratos/${contratoId}/parcelas`);
      setData(r.data);
    } catch {
      // silencioso
    } finally {
      setLoading(false);
    }
  }, [contratoId]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/contratos/${contratoId}/parcelas`);
        if (alive) setData(r.data);
      } catch {
        // silencioso
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [contratoId]);

  const startEdit = (p) => {
    setEditingId(p.id);
    setEditValor(String(p.valor ?? ""));
    setEditVenc((p.data_vencimento || "").slice(0, 10));
  };
  const cancelEdit = () => { setEditingId(null); setEditValor(""); setEditVenc(""); };

  const saveEdit = async (p) => {
    const valorNum = parseFloat(editValor);
    if (!valorNum || valorNum <= 0) { toast.error("Valor inválido"); return; }
    if (!editVenc) { toast.error("Data de vencimento obrigatória"); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/contratos/${contratoId}/parcelas/${p.id}`, {
        valor: valorNum,
        data_vencimento: editVenc,
      });
      toast.success("Parcela atualizada");
      cancelEdit();
      await reload();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Erro ao atualizar parcela"));
    } finally {
      setSaving(false);
    }
  };

  const removeParcela = async (p) => {
    if (!window.confirm(`Remover esta parcela (${formatCurrency(p.valor)})?`)) return;
    setSaving(true);
    try {
      await axios.delete(`${API}/contratos/${contratoId}/parcelas/${p.id}`);
      toast.success("Parcela removida");
      await reload();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Erro ao remover parcela"));
    } finally {
      setSaving(false);
    }
  };

  const addParcela = async () => {
    const valorNum = parseFloat(newValor);
    if (!valorNum || valorNum <= 0) { toast.error("Valor inválido"); return; }
    if (!newVenc) { toast.error("Data de vencimento obrigatória"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/contratos/${contratoId}/parcelas`, {
        valor: valorNum,
        data_vencimento: newVenc,
        parcela: newRotulo || "Extra",
      });
      toast.success("Parcela adicionada");
      setAdding(false); setNewValor(""); setNewVenc(""); setNewRotulo("");
      await reload();
    } catch (e) {
      toast.error(extractErrorMessage(e, "Erro ao adicionar parcela"));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;
  if (!data) return null;
  const { parcelas, resumo } = data;
  const hasParcelas = (parcelas?.length ?? 0) > 0;

  const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
  const fmtDate = (d) => {
    if (!d) return "-";
    return new Date(d + "T00:00:00").toLocaleDateString("pt-BR");
  };
  const STATUS_CFG = {
    pago: { label: "Pago", color: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50", icon: Check },
    vencida: { label: "Vencida", color: "bg-red-500", text: "text-red-700", bg: "bg-red-50", icon: AlertCircle },
    pendente: { label: "Pendente", color: "bg-amber-500", text: "text-amber-700", bg: "bg-amber-50", icon: Clock },
  };

  return (
    <Card className="border-0 shadow-sm" data-testid="cronograma-financeiro">
      <CardHeader className="pb-2 flex flex-row items-center justify-between">
        <CardTitle className="text-lg flex items-center gap-2">
          <DollarSign className="h-4 w-4" /> Cronograma Financeiro
        </CardTitle>
        {editavel && !adding && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setAdding(true)}
            data-testid="btn-adicionar-parcela"
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Adicionar parcela
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Resumo */}
        {hasParcelas && (
          <>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="p-2 rounded bg-emerald-50 text-center">
                <p className="text-emerald-700 font-medium">{resumo.qtd_pagas} pagas</p>
                <p className="font-mono text-emerald-800">{fmt(resumo.total_pago)}</p>
              </div>
              <div className="p-2 rounded bg-red-50 text-center">
                <p className="text-red-700 font-medium">{resumo.qtd_vencidas} vencidas</p>
                <p className="font-mono text-red-800">{fmt(resumo.total_vencido)}</p>
              </div>
              <div className="p-2 rounded bg-amber-50 text-center">
                <p className="text-amber-700 font-medium">{resumo.qtd_pendentes - resumo.qtd_vencidas} a vencer</p>
                <p className="font-mono text-amber-800">{fmt(resumo.total_pendente - resumo.total_vencido)}</p>
              </div>
            </div>

            {/* Progresso */}
            <div>
              <div className="flex justify-between text-[10px] mb-1 text-slate-500">
                <span>Progresso do contrato</span>
                <span>{resumo.progresso_pct}%</span>
              </div>
              <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${resumo.progresso_pct}%` }} />
              </div>
            </div>
          </>
        )}

        {/* Form de adicionar */}
        {adding && (
          <div className="p-3 rounded border border-dashed border-slate-300 bg-slate-50 space-y-2" data-testid="form-nova-parcela">
            <div className="grid grid-cols-3 gap-2">
              <Input
                type="number"
                step="0.01"
                placeholder="Valor"
                value={newValor}
                onChange={(e) => setNewValor(e.target.value)}
                data-testid="input-nova-parcela-valor"
              />
              <Input
                type="date"
                value={newVenc}
                onChange={(e) => setNewVenc(e.target.value)}
                data-testid="input-nova-parcela-venc"
              />
              <Input
                placeholder="Rótulo (opcional)"
                value={newRotulo}
                onChange={(e) => setNewRotulo(e.target.value)}
                data-testid="input-nova-parcela-rotulo"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setNewValor(""); setNewVenc(""); setNewRotulo(""); }}>Cancelar</Button>
              <Button size="sm" onClick={addParcela} disabled={saving} data-testid="btn-salvar-nova-parcela">Adicionar</Button>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!hasParcelas && !adding && (
          <p className="text-sm text-slate-500 text-center py-4">
            Nenhuma parcela cadastrada. {editavel && "Clique em \"Adicionar parcela\" para iniciar."}
          </p>
        )}

        {/* Timeline */}
        {hasParcelas && (
          <div className="space-y-1.5 max-h-96 overflow-y-auto pr-1">
            {parcelas.map((p) => {
              const cfg = STATUS_CFG[p.status_calc] || STATUS_CFG.pendente;
              const Icon = cfg.icon;
              const isEditing = editingId === p.id;
              const podeEditar = editavel && p.status_calc !== "pago";
              return (
                <div key={p.id} className={`flex items-center gap-2 p-2 rounded border ${cfg.bg}`} data-testid={`parcela-${p.id}`}>
                  <div className={`h-7 w-7 rounded-full ${cfg.color} flex items-center justify-center flex-shrink-0`}>
                    <Icon className="h-3.5 w-3.5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    {isEditing ? (
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          type="number"
                          step="0.01"
                          value={editValor}
                          onChange={(e) => setEditValor(e.target.value)}
                          className="h-8 text-xs"
                          data-testid={`input-edit-valor-${p.id}`}
                        />
                        <Input
                          type="date"
                          value={editVenc}
                          onChange={(e) => setEditVenc(e.target.value)}
                          className="h-8 text-xs"
                          data-testid={`input-edit-venc-${p.id}`}
                        />
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-xs font-semibold text-slate-700">{p.parcela || p.descricao || "Parcela"}</p>
                          <p className="font-mono text-xs font-bold text-slate-800">{fmt(p.valor)}</p>
                        </div>
                        <div className="flex items-center justify-between text-[10px] text-slate-500 mt-0.5">
                          <span>Venc {fmtDate(p.data_vencimento)}</span>
                          <span className={cfg.text}>
                            {p.status_calc === "pago" && p.data_pagamento
                              ? `Pago em ${fmtDate(p.data_pagamento)}`
                              : cfg.label}
                          </span>
                        </div>
                      </>
                    )}
                  </div>
                  {podeEditar && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {isEditing ? (
                        <>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => saveEdit(p)} disabled={saving} data-testid={`btn-salvar-parcela-${p.id}`}>
                            <Check className="h-3.5 w-3.5 text-emerald-700" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={cancelEdit}>
                            <X className="h-3.5 w-3.5 text-slate-500" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => startEdit(p)} data-testid={`btn-editar-parcela-${p.id}`}>
                            <Edit className="h-3.5 w-3.5 text-slate-600" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => removeParcela(p)} disabled={saving} data-testid={`btn-remover-parcela-${p.id}`}>
                            <Trash2 className="h-3.5 w-3.5 text-red-600" />
                          </Button>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
